import { useEffect, useState } from 'react';

interface Args {
  hintCount: number;
  exerciseKey: string;                 // reset timeline on new exercise
}

interface TimelineState {
  hintTier: number;                    // -1 = dialogue, 0..n-1 = hint index
  maxTierSeen: number;                 // highest tier reached so far this exercise
  advance: () => void;                 // reveal next hint tier (manual only)
  rewind: () => void;                  // step back to previous tier
  canAdvance: boolean;
  canRewind: boolean;
}

// Hint reveal is entirely manual — there is no timer-based auto-reveal. Kids
// who like to think are not pressured; they must ask for a tip explicitly.
export function useCalloutTimeline(args: Args): TimelineState {
  const { hintCount, exerciseKey } = args;
  const [hintTier, setHintTier] = useState(-1);
  const [maxTierSeen, setMaxTierSeen] = useState(-1);

  useEffect(() => {
    setHintTier(-1);
    setMaxTierSeen(-1);
  }, [exerciseKey]);

  function advance() {
    const next = Math.min(hintTier + 1, hintCount - 1);
    if (next !== hintTier && next >= 0) {
      setHintTier(next);
      setMaxTierSeen((cur) => Math.max(cur, next));
    }
  }

  function rewind() {
    const prev = Math.max(hintTier - 1, -1);
    if (prev !== hintTier) setHintTier(prev);
  }

  return {
    hintTier,
    maxTierSeen,
    advance,
    rewind,
    canAdvance: hintTier < hintCount - 1 && hintCount > 0,
    canRewind: hintTier > -1,
  };
}
